import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Calendar, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { Card } from './_shared.jsx';

/**
 * Per-agent Google Calendar connect card. Each agent connects their own
 * Google account — dialer-booked appointments are pushed to their primary
 * calendar with lead-email invites + reminders.
 */
export default function GoogleCalendarCard() {
  const location = useLocation();
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    apiGet('/calendar/connection').then((r) => {
      if (r?.ok) setConn(r);
    }).catch(() => setConn({ ok: true, connected: false }));
  };

  useEffect(() => { load(); }, []);

  // The OAuth callback redirects to /settings?calendar=connected|error
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('calendar') === 'connected') {
      toast.success('Google Calendar connected');
      load();
    } else if (params.get('calendar') === 'error') {
      toast.error(`Google Calendar connect failed: ${params.get('reason') || 'unknown'}`);
    }
  }, [location.search]);

  async function handleConnect() {
    setBusy(true);
    try {
      const r = await apiGet('/calendar/oauth/start');
      if (r?.ok && r.url) window.location.href = r.url;
      else toast.error('Could not start Google OAuth — set GOOGLE_CLIENT_ID / GOOGLE_REDIRECT_URI.');
    } catch (e) {
      toast.error(e.message || 'Failed to start OAuth');
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm('Disconnect Google Calendar? New appointments will no longer sync.')) return;
    setBusy(true);
    try {
      await apiPost('/calendar/disconnect', {});
      toast.success('Disconnected');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  }

  const connected = !!conn?.connected;

  return (
    <Card
      title="Google Calendar"
      description="Push dialer-booked appointments straight to your Google Calendar. The lead receives an invite (if their email is on file); you get 1-day email + 15-min popup reminders."
    >
      <div className="flex items-center justify-between gap-3 p-4 rounded-lg bg-civix-panelLight border border-civix-border/60">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Calendar className="w-4 h-4 text-civix-purpleSoft mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-sm flex items-center gap-2">
              Google Calendar
              {connected ? (
                <span className="pill bg-emerald-500/15 text-emerald-300 border-emerald-500/30 inline-flex items-center gap-1 text-[10px]">
                  <CheckCircle2 className="w-3 h-3" /> Connected
                </span>
              ) : (
                <span className="pill bg-white/5 text-white/50 border-white/10 text-[10px]">Not connected</span>
              )}
            </div>
            <div className="text-xs text-white/45 mt-0.5">
              {connected
                ? <>Connected as <strong>{conn.email || conn.name}</strong></>
                : 'Connect to auto-push dialer-booked appointments to your calendar.'}
            </div>
          </div>
        </div>
        {connected ? (
          <button
            type="button"
            onClick={handleDisconnect}
            disabled={busy}
            className="btn-ghost text-xs inline-flex items-center gap-1.5 shrink-0 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Disconnect
          </button>
        ) : (
          <button
            type="button"
            onClick={handleConnect}
            disabled={busy}
            className="btn-primary text-xs inline-flex items-center gap-1.5 shrink-0 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
            Connect Google Calendar
          </button>
        )}
      </div>
    </Card>
  );
}
